import * as Sentry from '@sentry/react';

/**
 * Frontend Sentry init.
 *
 * No-ops when VITE_SENTRY_DSN_FRONTEND is not set, so local dev and
 * deploys without Sentry configured stay silent. Once the env var is
 * dropped into Cloudflare Pages, the next deploy turns it on without a
 * code change.
 */

let initialised = false;

export function initSentry(): void {
  if (initialised) return;
  initialised = true;

  const dsn = import.meta.env.VITE_SENTRY_DSN_FRONTEND;
  if (!dsn) {
    if (import.meta.env.DEV) {
      console.info('[sentry] VITE_SENTRY_DSN_FRONTEND not set - Sentry disabled');
    }
    return;
  }

  Sentry.init({
    dsn,
    environment: import.meta.env.MODE,
    release: import.meta.env.VITE_RELEASE_SHA,
    tracesSampleRate: 0.1,
    replaysSessionSampleRate: 0,
    replaysOnErrorSampleRate: 1.0,
    integrations: [
      Sentry.browserTracingIntegration(),
      Sentry.replayIntegration({ maskAllText: false, blockAllMedia: false }),
    ],
  });
}

/**
 * Drop a structured breadcrumb. Safe to call before init() - Sentry
 * buffers breadcrumbs internally and a no-init call is a cheap no-op.
 */
export function breadcrumb(category: string, message: string, data?: Record<string, unknown>): void {
  if (!initialised) return;
  Sentry.addBreadcrumb({ category, message, data, level: 'info' });
}

/**
 * Manually capture an error. Use this from `catch` blocks where the
 * error is meaningful but not thrown further (e.g. background tasks
 * where we recover gracefully but want visibility).
 */
export function captureError(err: unknown, context?: Record<string, unknown>): void {
  if (!initialised) {
    console.error('[sentry-disabled] error not reported:', err, context);
    return;
  }
  Sentry.captureException(err, { extra: context });
}
